import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { useAuth } from './useAuth.js'
import { apiFetch, setStudentView } from '../lib/api.js'

interface PreviewModeContextValue {
  /** Whether admin preview features are active (false when student view is on) */
  isPreviewMode: boolean
  previewLoading: boolean
  /** Whether the logged-in user is an admin (regardless of view mode) */
  isAdmin: boolean
  /** Whether the admin is viewing as a student */
  isStudentView: boolean
  /** Toggle between admin view and student view */
  toggleStudentView: () => void
}

const PreviewModeContext = createContext<PreviewModeContextValue>({
  isPreviewMode: false,
  previewLoading: false,
  isAdmin: false,
  isStudentView: false,
  toggleStudentView: () => {},
})

interface MeResponse {
  user: { id: string; email: string }
  isPanelUser: boolean
  profile: { role: string; isActive: boolean } | null
}

export function PreviewModeProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth()
  const [isAdmin, setIsAdmin] = useState(false)
  const [isStudentView, setIsStudentView] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(!!session?.access_token)

  useEffect(() => {
    if (!session?.access_token) {
      setIsAdmin(false)
      setIsStudentView(false)
      setPreviewLoading(false)
      return
    }

    setPreviewLoading(true)
    apiFetch<MeResponse>('/api/auth/me')
      .then((data) => {
        setIsAdmin(data.isPanelUser === true)
      })
      .catch(() => {
        setIsAdmin(false)
      })
      .finally(() => {
        setPreviewLoading(false)
      })
  }, [session?.access_token])

  const toggleStudentView = useCallback(() => {
    setIsStudentView(prev => !prev)
  }, [])

  useEffect(() => {
    setStudentView(isStudentView)
  }, [isStudentView])

  const isPreviewMode = isAdmin && !isStudentView

  return (
    <PreviewModeContext.Provider value={{ isPreviewMode, previewLoading, isAdmin, isStudentView, toggleStudentView }}>
      {children}
    </PreviewModeContext.Provider>
  )
}

export function usePreviewMode(): PreviewModeContextValue {
  return useContext(PreviewModeContext)
}
